// GtNotifyBell — the bell in the left nav. Shows an unread count, opens a panel
// of grouped notifications (by kind, role or client, and day), each of which
// opens to list the people inside it, every one linking to their record.
// Read, unread and clear per group; mark all read, clear read and clear all.
// C1-SHARECHECK: a recruiter call owed (follow_up) is pinned at the top, cannot
// be cleared, and opens to a note box with Call done.
import { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from '../config';

const POLL_MS = 60000;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

function ago(v) {
  const t = v ? new Date(v).getTime() : NaN;
  if (isNaN(t)) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return m + ' min ago';
  const h = Math.round(m / 60);
  if (h < 24) return h + (h === 1 ? ' hour ago' : ' hours ago');
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return d + ' days ago';
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const BELL = 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0';

export default function GtNotifyBell({ getToken, navigate }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [tab, setTab] = useState('all');
  const [groups, setGroups] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [items, setItems] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [moreBusy, setMoreBusy] = useState(false);
  const [notes, setNotes] = useState({});
  const [doneBusy, setDoneBusy] = useState(null);
  const panelRef = useRef(null);
  const bellRef = useRef(null);

  const api = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.message || body.error || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  const loadCount = useCallback(async () => {
    try { const r = await api('/api/notifications/count'); setUnread(r.unread || 0); } catch (e) { /* the badge is not worth an error */ }
  }, [api]);

  const loadList = useCallback(async () => {
    setError(null);
    try {
      const r = await api('/api/notifications?tab=' + tab);
      setGroups(r.groups || []);
      setUnread(r.unread || 0);
      setHasMore(!!r.has_more);
    } catch (e) { setError(e.message); setGroups([]); }
  }, [api, tab]);

  useEffect(() => {
    loadCount();
    const t = setInterval(() => { if (!document.hidden) loadCount(); }, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (bellRef.current && bellRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  async function mark(keys, state) {
    try {
      await api('/api/notifications/mark', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ keys, state }) });
      await loadList();
    } catch (e) { setError(e.message); }
  }

  async function bulk(path) {
    setBusy(true);
    try { await api(path, { method: 'POST' }); setExpanded(null); await loadList(); } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function loadOlder() {
    setMoreBusy(true);
    try {
      const r = await api('/api/notifications?tab=' + tab + '&offset=' + (groups || []).length);
      setGroups(prev => (prev || []).concat(r.groups || []));
      setUnread(r.unread || 0);
      setHasMore(!!r.has_more);
    } catch (e) { setError(e.message); }
    setMoreBusy(false);
  }

  async function morePeople(key) {
    const cur = items[key];
    if (!cur || cur.busy) return;
    setItems(prev => ({ ...prev, [key]: { ...cur, busy: true } }));
    try {
      const r = await api('/api/notifications/items?key=' + encodeURIComponent(key) + '&offset=' + cur.next);
      setItems(prev => ({ ...prev, [key]: { list: cur.list.concat(r.items || []), more: !!r.has_more, next: r.next_offset, busy: false } }));
    } catch (e) { setItems(prev => ({ ...prev, [key]: { ...cur, busy: false } })); setError(e.message); }
  }

  async function callDone(g) {
    setDoneBusy(g.key); setError(null);
    try {
      await api('/api/follow-ups/' + g.follow_up.id + '/done', { method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({ note: (notes[g.key] || '').trim() || null }) });
      setExpanded(null);
      setNotes((n) => { const x = { ...n }; delete x[g.key]; return x; });
      await loadList();
    } catch (e) { setError(e.message); }
    setDoneBusy(null);
  }

  async function toggle(g) {
    if (expanded === g.key) { setExpanded(null); return; }
    setExpanded(g.key);
    if (!g.read) mark([g.key], 'read');
    if (g.follow_up) return;
    if (!items[g.key]) {
      try {
        const r = await api('/api/notifications/items?key=' + encodeURIComponent(g.key));
        setItems(prev => ({ ...prev, [g.key]: { list: r.items || [], more: !!r.has_more, next: r.next_offset, busy: false } }));
      } catch (e) { setItems(prev => ({ ...prev, [g.key]: { list: [], more: false, next: 0, busy: false } })); setError(e.message); }
    }
  }

  function go(link) {
    if (!link) return;
    setOpen(false);
    navigate(link);
  }

  const list = groups || [];
  const anyRead = list.some(g => g.read);

  return (
    <>
      <button ref={bellRef} type="button" className={'gt-nav-item gtnb-bell' + (open ? ' gt-nav-item--active' : '')}
        onClick={() => setOpen(v => !v)} aria-label={unread ? unread + ' unread notifications' : 'Notifications'}>
        <span className="gtnb-icwrap">
          <svg className="gt-nav-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {BELL.split(' M').map((d, i) => <path key={i} d={(i ? 'M' : '') + d} />)}
          </svg>
          {unread > 0 && <span className="gtnb-badge">{unread > 99 ? '99+' : unread}</span>}
        </span>
        Alerts
      </button>

      {open && (
        <div ref={panelRef} className="gtnb-panel" role="dialog" aria-label="Notifications">
          <div className="gtnb-head">
            <div className="gtnb-title">Notifications</div>
            <button type="button" className="gtnb-x" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </div>
          <div className="gtnb-bar">
            <div className="gtnb-tabs">
              <button type="button" className={'gtnb-tab' + (tab === 'all' ? ' gtnb-tab--on' : '')} onClick={() => setTab('all')}>All</button>
              <button type="button" className={'gtnb-tab' + (tab === 'unread' ? ' gtnb-tab--on' : '')} onClick={() => setTab('unread')}>
                Unread{unread ? ' · ' + unread : ''}
              </button>
            </div>
            <div className="gtnb-actions">
              <button type="button" className="gtnb-link" disabled={busy || !unread} onClick={() => bulk('/api/notifications/read-all')}>Mark all read</button>
              <button type="button" className="gtnb-link" disabled={busy || !anyRead} onClick={() => bulk('/api/notifications/clear-read')}>Clear read</button>
              <button type="button" className="gtnb-link" disabled={busy || !list.length} onClick={() => bulk('/api/notifications/clear-all')}>Clear all</button>
            </div>
          </div>
          {error && <div className="gtnb-error">{error}</div>}
          <div className="gtnb-list">
            {groups === null ? <div className="gtnb-empty">Loading…</div>
              : list.length === 0 ? (
                <div className="gtnb-empty">{tab === 'unread' ? 'Nothing unread.' : 'Nothing new in the last 30 days.'}</div>
              ) : list.map(g => (
                <div key={g.key} className={'gtnb-item gtnb-tone-' + g.tone + (g.read ? '' : ' gtnb-item--unread') + (expanded === g.key ? ' gtnb-item--open' : '') + (g.pinned ? ' gtnb-item--pinned' : '')}>
                  <div className="gtnb-row">
                    <button type="button" className="gtnb-main" onClick={() => toggle(g)} aria-expanded={expanded === g.key}>
                      <span className="gtnb-dot" aria-hidden="true" />
                      <span className="gtnb-text">
                        <span className="gtnb-t">{g.title}</span>
                        <span className="gtnb-s">{[g.sub, ago(g.latest_at)].filter(Boolean).join(' · ')}</span>
                      </span>
                    </button>
                    <span className="gtnb-tools">
                      <button type="button" className="gtnb-tool" title={g.read ? 'Mark as unread' : 'Mark as read'}
                        onClick={() => mark([g.key], g.read ? 'unread' : 'read')}>{g.read ? '○' : '●'}</button>
                      {g.pinned
                        ? <span className="gtnb-pin" title="Stays here until the call is marked done">{g.follow_up && g.follow_up.overdue ? 'Late' : 'Due'}</span>
                        : <button type="button" className="gtnb-tool" title="Clear" onClick={() => mark([g.key], 'cleared')}>×</button>}
                    </span>
                  </div>
                  {expanded === g.key && g.follow_up && (
                    <div className="gtnb-sub gtnb-call">
                      <textarea className="gtnb-call-note" rows={2} maxLength={2000} placeholder="What did you agree? (optional)"
                        value={notes[g.key] || ''} onChange={(e) => setNotes((n) => ({ ...n, [g.key]: e.target.value }))} />
                      <div className="gtnb-call-act">
                        <button type="button" className="gtnb-call-done" disabled={doneBusy === g.key} onClick={() => callDone(g)}>
                          {doneBusy === g.key ? 'Saving…' : 'Call done'}
                        </button>
                        {g.link && <button type="button" className="gtnb-open" onClick={() => go(g.link)}>Open record</button>}
                      </div>
                    </div>
                  )}
                  {expanded === g.key && !g.follow_up && (
                    <div className="gtnb-sub">
                      {!items[g.key] ? <div className="gtnb-empty gtnb-empty--sm">Loading…</div>
                        : items[g.key].list.length === 0 ? <div className="gtnb-empty gtnb-empty--sm">Nothing to show.</div>
                        : (
                          <div className="gtnb-people">
                            {items[g.key].list.map((x, i) => (
                              <button key={i} type="button" className={'gtnb-person' + (x.link ? '' : ' gtnb-person--plain')} disabled={!x.link} onClick={() => go(x.link)}>
                                <span className="gtnb-pn">{x.name}</span>
                                <span className="gtnb-pm">{[x.note, ago(x.at)].filter(Boolean).join(' · ')}</span>
                              </button>
                            ))}
                            {items[g.key].more && (
                              <button type="button" className="gtnb-more" disabled={items[g.key].busy} onClick={() => morePeople(g.key)}>
                                {items[g.key].busy ? 'Loading…' : 'Show more'}
                              </button>
                            )}
                          </div>
                        )}
                      {g.link && <button type="button" className="gtnb-open" onClick={() => go(g.link)}>Open</button>}
                    </div>
                  )}
                </div>
              ))}
            {hasMore && (
              <button type="button" className="gtnb-more gtnb-more--older" disabled={moreBusy} onClick={loadOlder}>
                {moreBusy ? 'Loading…' : 'Show older'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
